/**
 * First-party behavioral event logger — writes to the `user_events` table
 * (migration 026). Unlike the GA4 helpers in shared/utils/analytics.ts, these
 * events stay in our own Postgres and power ranking, personalization, and the
 * conversion signal in get_referrer_rank_scores().
 *
 * Design constraints:
 *  - Fire-and-forget: never throws, never blocks UI, silently no-ops when the
 *    user is signed out or the table doesn't exist yet.
 *  - Batched: events are buffered and flushed together to avoid a network
 *    round-trip per click.
 *  - Deduplicated: identical events within a short window are dropped
 *    (e.g. re-renders firing view_profile twice).
 */

import { supabase } from '../lib/supabaseClient';
import { getAcquisitionParams } from '../utils/utmTracker';

export type UserEventType =
  | 'search'
  | 'view_profile'
  | 'select_professional'
  | 'book'
  | 'complete'
  | 'rate'
  | 'click_featured'
  | 'grade_resume'
  | 'checkout_start'
  | 'payment_success'
  | 'payment_failed'
  | 'sla_breach'
  | 'share_referral'
  | 'apply_drop'
  | (string & {});

export type UserEventTargetType = 'referrer' | 'job' | 'service' | (string & {});

interface PendingEvent {
  event_type: string;
  target_type: string | null;
  target_id: string | null;
  metadata: Record<string, unknown>;
}

const FLUSH_INTERVAL_MS = 3_000;
const MAX_BUFFER = 20;
const DEDUPE_WINDOW_MS = 15_000;
const MAX_METADATA_CHARS = 2_000;

let buffer: PendingEvent[] = [];
let flushTimer: ReturnType<typeof setTimeout> | null = null;
let disabled = false; // set when the table is missing so we stop retrying
const recentKeys = new Map<string, number>();

function dedupeKey(e: PendingEvent): string {
  return `${e.event_type}|${e.target_type ?? ''}|${e.target_id ?? ''}|${JSON.stringify(e.metadata)}`;
}

function sanitizeMetadata(metadata?: Record<string, unknown>): Record<string, unknown> {
  if (!metadata) return {};
  try {
    const json = JSON.stringify(metadata);
    if (json.length <= MAX_METADATA_CHARS) return metadata;
    return { truncated: true };
  } catch {
    return {};
  }
}

async function flush(): Promise<void> {
  if (flushTimer) {
    clearTimeout(flushTimer);
    flushTimer = null;
  }
  if (disabled || buffer.length === 0) return;

  const batch = buffer;
  buffer = [];

  try {
    const { data: sessionData } = await supabase.auth.getSession();
    const userId = sessionData?.session?.user?.id;
    if (!userId) return; // signed out — drop silently, RLS would reject anyway

    const rows = batch.map((e) => ({ ...e, user_id: userId }));
    const { error } = await supabase.from('user_events').insert(rows);
    if (error) {
      // 42P01 = relation does not exist (migration not applied yet) — disable for session
      if ((error as any).code === '42P01') disabled = true;
    }
  } catch {
    /* never surface logging failures */
  }
}

function scheduleFlush() {
  if (buffer.length >= MAX_BUFFER) {
    void flush();
    return;
  }
  if (!flushTimer) {
    flushTimer = setTimeout(() => void flush(), FLUSH_INTERVAL_MS);
  }
}

/**
 * Log a behavioral event. Safe to call from anywhere — never throws, never blocks.
 *
 *   logEvent('view_profile', 'referrer', professional.id);
 *   logEvent('search', null, null, { query, results: n });
 */
export function logEvent(
  eventType: UserEventType,
  targetType?: UserEventTargetType | null,
  targetId?: string | null,
  metadata?: Record<string, unknown>
): void {
  if (disabled || typeof window === 'undefined') return;

  const acqParams = getAcquisitionParams();
  const enrichedMetadata: Record<string, unknown> = {
    ...metadata,
    ...(acqParams.utm_source ? { utm_source: acqParams.utm_source } : {}),
    ...(acqParams.utm_medium ? { utm_medium: acqParams.utm_medium } : {}),
    ...(acqParams.ref_code ? { ref_code: acqParams.ref_code } : {}),
  };

  const event: PendingEvent = {
    event_type: String(eventType).slice(0, 64),
    target_type: targetType ? String(targetType).slice(0, 32) : null,
    target_id: targetId || null,
    metadata: sanitizeMetadata(enrichedMetadata),
  };

  // Drop identical events fired within the dedupe window (re-renders, double clicks)
  const key = dedupeKey(event);
  const now = Date.now();
  const lastSeen = recentKeys.get(key);
  if (lastSeen && now - lastSeen < DEDUPE_WINDOW_MS) return;
  recentKeys.set(key, now);
  if (recentKeys.size > 200) {
    for (const [k, t] of recentKeys) {
      if (now - t > DEDUPE_WINDOW_MS) recentKeys.delete(k);
    }
  }

  buffer.push(event);
  scheduleFlush();
}

/** Flush pending events immediately (used on pagehide/visibilitychange). */
export function flushEvents(): void {
  void flush();
}

if (typeof window !== 'undefined') {
  window.addEventListener('pagehide', () => void flush());
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'hidden') void flush();
  });
}
